// Define all report type options
const MARKETPLACE_SELLER_LOOKUP_OPTION = {
  label: "Marketplace seller information",
  description:
    "Gather a list of marketplace sellers information (marketplace seller name and address)",
  dataTileInput: "Seller name, Tax ID (optional), Seller ID (optional)",
  dataTileOutput: "Name, CPF/CNPJ, Address",
  value: "BrazilMarketPlaceSellerLookup",
  disabled: false,
  isCritical: true,
};

const SELLER_INFORMATION_LOOKUP_OPTION = {
  label: "Seller information lookup",
  description:
    "Retrieve information on Amazon Marketplace 3P sellers (name, address)",
  dataTileInput: "Marketplace ID, Seller IDs",
  dataTileOutput: "Seller name, Address",
  value: "SellerInformationLookup",
  disabled: false,
  isCritical: true,
};

const TRANSACTION_REPORT_OPTION = {
  label: "Transaction report",
  description: "Gather a list of seller transactions in Brazil",
  dataTileInput: "Legal Entity, Seller State, Tax ID",
  dataTileOutput:
    "3P seller information, buyer information, order information and invoice information",
  value: "TransactionReport",
  disabled: true,
  isCritical: false,
};

const CUSTOMER_LOOKUP_OPTION = {
  label: "Customer information lookup",
  description:
    "Gather a list of customer/buyer information (customer name and address)",
  dataTileInput: "Customer ID or Address ID",
  dataTileOutput: "Customer name, Address",
  value: "CustomerInformationLookupByBraavos",
  disabled: false,
  isCritical: true,
};

const BULK_UPLOAD_WORKFLOW_OPTION = {
  label: "Bulk upload audits",
  description: "Create audits by uploading an excel file",
  dataTileInput: "Excel file",
  dataTileOutput: "Audits",
  value: "BulkUploadWorkflow",
  disabled: false,
  isCritical: false,
};

const BULK_UPLOAD_NOTICES_OPTION = {
  label: "Bulk upload notices",
  description: "Create notices by uploading an excel file",
  dataTileInput: "Excel file",
  dataTileOutput: "Notices",
  value: "BulkUploadWorkflow",
  disabled: false,
  isCritical: false,
};

const BULK_UPLOAD_LITIGATIONS_OPTION = {
  label: "Bulk upload litigations",
  description: "Create litigations by uploading an excel file",
  dataTileInput: "Excel file",
  dataTileOutput: "Litigations",
  value: "BulkUploadWorkflow",
  disabled: false,
  isCritical: false,
};

const BULK_UPLOAD_TASKS_OPTION = {
  label: "Bulk upload taskgroups and tasks",
  description: "Create tasks by uploading an excel file",
  dataTileInput: "Excel file",
  dataTileOutput: "Taskgroups and tasks",
  value: "BulkCreateTasksWorkflow",
  disabled: false,
  isCritical: false,
};

const BULK_CREATE_TASKS_FOR_TASKGROUPS_OPTION = {
  label: "Bulk upload tasks",
  description:
    "Create tasks for existing taskgroups by uploading an excel file",
  dataTileInput: "Excel file",
  dataTileOutput: "Tasks",
  value: "BulkCreateTaskGroupsWorkflow",
  disabled: false,
  isCritical: false,
};

const COPY_PAYMENTS_OPTION = {
  label: "Copy payments to next year",
  description:
    "Bulk copy existing payment structures to the next year for selected notices. All values are carried over — only dates are incremented.",
  dataTileInput: "Selected notices",
  dataTileOutput: "Copied payments (year +1)",
  value: "PaymentRollForwardWorkflow",
  disabled: false,
  isCritical: false,
};

export const typeToReportName = (
  _teamName: string,
  typeName: string,
  item?: any,
) => {
  // Handle TeamTasksExportWorkflow
  if (typeName === "TeamTasksExportWorkflow") {
    return "Team tasks export";
  }
  if (typeName === "TeamFilesImportWorkflow") {
    return "Team files import";
  }
  if (typeName === "TeamFilesExportWorkflow") {
    return "Team files export";
  }

  // Handle the special case where all bulk upload types use the same workflow name
  if (typeName === "BulkUploadWorkflow" && item) {
    const querySqlParameterMap = item.querySqlParameterMap || {};
    if (querySqlParameterMap.workflowType) {
      return querySqlParameterMap.workflowType;
    }

    const queryName = (item.queryName || "").toLowerCase();
    if (queryName.includes("notice")) {
      return "Bulk upload notices";
    }
    if (queryName.includes("litigation")) {
      return "Bulk upload litigations";
    }
    if (queryName.includes("audit")) {
      return "Bulk upload audits";
    }
    // Default to audits if we can't determine the type
    return "Bulk upload audits";
  }

  return (
    ALL_DATA_WORKFLOWS.find((item) => item.value === typeName)?.label ??
    typeName
  );
};

// All new workflows must be added here in order.
const ALL_DATA_WORKFLOWS = [
  MARKETPLACE_SELLER_LOOKUP_OPTION,
  TRANSACTION_REPORT_OPTION,
  CUSTOMER_LOOKUP_OPTION,
  SELLER_INFORMATION_LOOKUP_OPTION,
  BULK_UPLOAD_WORKFLOW_OPTION,
  BULK_UPLOAD_NOTICES_OPTION,
  BULK_UPLOAD_LITIGATIONS_OPTION,
  BULK_UPLOAD_TASKS_OPTION,
  BULK_CREATE_TASKS_FOR_TASKGROUPS_OPTION,
  COPY_PAYMENTS_OPTION,
];

// Teams with the customer information lookup. The workflow tile, search types and
// search-by-id options all read this list, so a team is added once.
const CUSTOMER_LOOKUP_TEAMS = new Set([
  "advertising",
  "gaia_dev",
  "us_salt_direct",
  "us_salt_indirect",
  "canada",
  "tax_engine",
  "gtd_audit",
  "tax_controversy",
  "emea_stores",
  "aws_latam_tax_planning",
  "digital_devices",
  "international_tax_planning_mexico",
  "itx_compliance",
]);

export interface DataWorkflowFlags {
  isSellerInfoSearchWorkflowEnabled?: boolean;
  isBulkUploadAuditsEnabled?: boolean;
  isBulkUploadNoticesEnabled?: boolean;
  isBulkUploadLitigationsEnabled?: boolean;
  isPaymentRollForwardEnabled?: boolean;
}

// Define all report type options at team level on report selection page
const getReportTypesOnTeam = (
  teamName: string,
  {
    isSellerInfoSearchWorkflowEnabled,
    isBulkUploadAuditsEnabled,
    isBulkUploadNoticesEnabled,
    isBulkUploadLitigationsEnabled,
    isPaymentRollForwardEnabled,
  }: DataWorkflowFlags,
) => {
  // Step 1: Get existing team-based list (current prod behavior)
  let teamBaseOptions: typeof ALL_DATA_WORKFLOWS = [];
  switch (teamName) {
    case "gaia_dev":
      teamBaseOptions = [
        MARKETPLACE_SELLER_LOOKUP_OPTION,
        TRANSACTION_REPORT_OPTION,
        CUSTOMER_LOOKUP_OPTION,
      ];
      break;
    case "brazil_indirect":
      teamBaseOptions = [
        MARKETPLACE_SELLER_LOOKUP_OPTION,
        TRANSACTION_REPORT_OPTION,
      ];
      break;
    default:
      if (CUSTOMER_LOOKUP_TEAMS.has(teamName)) {
        teamBaseOptions = [CUSTOMER_LOOKUP_OPTION];
      }
  }

  // Step 2: Add bulk upload options
  if (isBulkUploadAuditsEnabled) {
    teamBaseOptions.push(BULK_UPLOAD_WORKFLOW_OPTION);
  }
  if (isBulkUploadNoticesEnabled) {
    teamBaseOptions.push(BULK_UPLOAD_NOTICES_OPTION);
  }
  if (isBulkUploadLitigationsEnabled) {
    teamBaseOptions.push(BULK_UPLOAD_LITIGATIONS_OPTION);
  }

  // Step 3: Check if seller info workflow is enabled and add seller info options
  if (isSellerInfoSearchWorkflowEnabled) {
    const hasSellerInfoLookup = teamBaseOptions.some(
      (option) => option.value === "SellerInformationLookup",
    );

    if (!hasSellerInfoLookup) {
      teamBaseOptions.push(SELLER_INFORMATION_LOOKUP_OPTION);
    }
  }

  // Step 4: Add the payment copy workflow
  if (isPaymentRollForwardEnabled) {
    teamBaseOptions.push(COPY_PAYMENTS_OPTION);
  }

  // Step 5: Use ALL_DATA_WORKFLOWS as default sequence and return based on that order
  const finalOptions = ALL_DATA_WORKFLOWS.filter((workflow) =>
    teamBaseOptions.some((teamOption) => teamOption.label === workflow.label),
  );

  return finalOptions;
};

export const getDataReportTypes = (
  teamName: string,
  isCriticalUser: boolean,
  parentId?: string,
  flags: DataWorkflowFlags = {},
) => {
  const reportTypes = getReportTypesOnTeam(teamName, flags);
  let filteredReportTypes = isCriticalUser
    ? reportTypes
    : reportTypes.filter((reportType) => !reportType.isCritical);

  // If parentId is provided and it's a valid audit ID, exclude the bulk upload workflow
  if (
    parentId &&
    (parentId.startsWith("audit") || parentId.match(/^[A-Z]\d{7}$/))
  ) {
    filteredReportTypes = filteredReportTypes.filter(
      (reportType) => reportType.value !== "BulkUploadWorkflow",
    );
  }

  return filteredReportTypes;
};

// Define constants to for all customer lookup search type options
const CUSTOMER_INFO_LOOKUP_OPTION = {
  value: "customerInfo",
  label: "Customer information",
  description: "Customer name",
  disabled: false,
};

const ADDRESS_INFO_LOOKUP_OPTION = {
  value: "addressInfo",
  label: "Address information",
  description: "Customer address details",
};

export const getCustomerInfoLookUpComponentTitle = (searchType: string) => {
  return searchType === "customerInfo"
    ? "Customer information"
    : "Address information";
};

export const getCustomerInfoSearchTypeLabel = (
  teamName: string,
  typeName: string,
) => {
  return (
    getCustomerInfoSearchTypesOnTeam(teamName).find(
      (item) => item.value === typeName,
    )?.label ?? typeName
  );
};

// Define customer lookup search types at team level
export const getCustomerInfoSearchTypesOnTeam = (teamName: string) =>
  CUSTOMER_LOOKUP_TEAMS.has(teamName)
    ? [CUSTOMER_INFO_LOOKUP_OPTION, ADDRESS_INFO_LOOKUP_OPTION]
    : [];

export const convertUnixEpoch = (timeStamp: number) => {
  return new Date(timeStamp).toISOString().slice(0, 19);
};

// Customer information search type configuration based on team
export interface SearchIdOption {
  value: string;
  key: string;
  label: string;
  description: string;
}

const BUYER_ID_SEARCH_OPTION = {
  value: "buyerIds",
  key: "buyerIds",
  label: "Buyer ID",
};

const BUYER_EXEMPTION_CERT_ID_SEARCH_OPTION = {
  value: "buyerExemptionCertIds",
  key: "buyerExemptionCertIds",
  label: "Buyer exemption certification ID",
};

const ADDRESS_ID_SEARCH_OPTION = {
  value: "addressIds",
  key: "addressIds",
  label: "Address ID",
};

export interface SearchIdOptions {
  customerInfo: Array<SearchIdOption>;
  addressInfo: Array<SearchIdOption>;
}

export const getSearchIdOptionsOnTeam = (teamName: string) =>
  CUSTOMER_LOOKUP_TEAMS.has(teamName)
    ? {
        customerInfo: [
          BUYER_ID_SEARCH_OPTION,
          BUYER_EXEMPTION_CERT_ID_SEARCH_OPTION,
        ],
        addressInfo: [ADDRESS_ID_SEARCH_OPTION],
      }
    : { customerInfo: [], addressInfo: [] };

// Define search by ids options based on searchType
export const getSearchIdOptionsOnSearchType = (
  teamName: string,
  searchType: string,
) => {
  return getSearchIdOptionsOnTeam(teamName)[
    searchType as keyof SearchIdOptions
  ].map((option) => ({
    value: option.value,
    label: option.label,
  }));
};

export const isAllowedOnQueryType = (
  teamName: string,
  isCriticalUser: boolean,
  queryType: string,
  parentId?: string,
  flags: DataWorkflowFlags = {},
) => {
  // Allow bulk task workflows for all teams
  if (
    queryType === "BulkCreateTasksWorkflow" ||
    queryType === "BulkCreateTaskGroupsWorkflow"
  ) {
    return true;
  }

  const res = getDataReportTypes(teamName, isCriticalUser, parentId, flags);
  return res.some((item) => item.value === queryType);
};

/**
 * This defines the workflow configuration page and critical data usage page header and description
 */
const CUSTOMER_INFORMATION_LOOKUP_PAGE_CONFIG = {
  pageHeader: "Customer information lookup",
  description:
    "Information on customer name and address. This is a PII (Personal Identifiable Information) workflow. Approval from critical data owners is required to proceed.",
};

const MARKETPLACE_SELLER_LOOKUP_PAGE_CONFIG = {
  pageHeader: "Marketplace seller information",
  description:
    "Create a list of marketplace sellers, including name, address, etc. Data source is ANDES and updated monthly.",
};

const SELLER_INFORMATION_LOOKUP_PAGE_CONFIG = {
  pageHeader: "Seller information lookup",
  description:
    "Retrieve information on Amazon Marketplace 3P sellers (name, address). Critical data access required.",
};

const PLACEHOLDER_PAGE_CONFIG = {
  pageHeader: "Placeholder for page header",
  description: "Placeholder for page header description",
};

export const getWorkflowConfigPageHeaderSettings = (
  queryType: string | null,
) => {
  if (!queryType) return PLACEHOLDER_PAGE_CONFIG;
  switch (queryType) {
    case "CustomerInformationLookupByBraavos":
      return CUSTOMER_INFORMATION_LOOKUP_PAGE_CONFIG;
    case "BrazilMarketPlaceSellerLookup":
      return MARKETPLACE_SELLER_LOOKUP_PAGE_CONFIG;
    case "SellerInformationLookup":
      return SELLER_INFORMATION_LOOKUP_PAGE_CONFIG;
    default:
      return PLACEHOLDER_PAGE_CONFIG;
  }
};
